import React, { useState } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { Drawer } from '../../design-system/Drawer';
import { Input, Textarea } from '../../design-system/Input';
import { Select } from '../../design-system/Dropdown';
import { Button } from '../../design-system/Button';
import { Badge } from '../../design-system/Badge';
import {
  CheckCircle2,
  AlertCircle,
  Clock,
  Trash2,
  Plus,
  MessageSquare,
  ListTodo,
  Layers,
  Tag,
  Bug,
  Sparkles,
  CheckSquare,
  Paperclip,
  Eye,
  Link2,
  AlertTriangle,
  FileText,
  Download,
  AtSign
} from 'lucide-react';

export const TaskDetailDrawer = ({ task, onClose }) => {
  const {
    updateTask,
    deleteTask,
    members = [],
    tasks = [],
    setSelectedTask
  } = useWorkspace();

  const [newChecklistText, setNewChecklistText] = useState('');
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
  const [newLabelText, setNewLabelText] = useState('');
  const [newCommentText, setNewCommentText] = useState('');
  const [isAddingLabel, setIsAddingLabel] = useState(false);
  const [isAddingDependency, setIsAddingDependency] = useState(false);
  const [depRelationship, setDepRelationship] = useState('blockedBy'); // 'blockedBy', 'blocking', 'relatedTo'
  const [selectedDepTaskId, setSelectedDepTaskId] = useState('');

  if (!task) return null;

  const statusOptions = [
    { value: 'backlog', label: 'Backlog', icon: <span className="material-symbols-outlined" style={{ fontSize: '15px' }}>inventory_2</span> },
    { value: 'todo', label: 'Todo', icon: <span className="material-symbols-outlined" style={{ fontSize: '15px' }}>radio_button_unchecked</span> },
    { value: 'in_progress', label: 'In Progress', icon: <span className="material-symbols-outlined" style={{ fontSize: '15px', color: 'var(--accent-teal)' }}>pending</span> },
    { value: 'review', label: 'Review', icon: <span className="material-symbols-outlined" style={{ fontSize: '15px', color: 'var(--color-purple)' }}>rate_review</span> },
    { value: 'done', label: 'Done', icon: <span className="material-symbols-outlined" style={{ fontSize: '15px', color: 'var(--color-success)' }}>check_circle</span> }
  ];

  const priorityOptions = [
    { value: 'urgent', label: 'Urgent', icon: <AlertCircle size={14} color="var(--color-danger)" /> },
    { value: 'high', label: 'High', icon: <AlertCircle size={14} color="var(--color-warning)" /> },
    { value: 'medium', label: 'Medium', icon: <Clock size={14} color="var(--color-info)" /> },
    { value: 'low', label: 'Low', icon: <CheckCircle2 size={14} color="var(--text-muted)" /> }
  ];

  const typeOptions = [
    { value: 'feature', label: 'Feature', icon: <Sparkles size={14} color="var(--accent-teal)" /> },
    { value: 'task', label: 'Task', icon: <CheckSquare size={14} color="var(--color-info)" /> },
    { value: 'bug', label: 'Bug', icon: <Bug size={14} color="var(--color-danger)" /> },
    { value: 'chore', label: 'Chore', icon: <Layers size={14} color="var(--text-muted)" /> }
  ];

  const estimateOptions = [
    { value: '1 pt', label: '1 pt (Trivial)' },
    { value: '2 pts', label: '2 pts (Small)' },
    { value: '3 pts', label: '3 pts (Medium)' },
    { value: '5 pts', label: '5 pts (Large)' },
    { value: '8 pts', label: '8 pts (Very Large)' },
    { value: '13 pts', label: '13 pts (Epic / Spike)' }
  ];

  const assigneeOptions = [
    { value: 'unassigned', label: 'Unassigned' },
    ...members.map((m) => ({
      value: m.user.name,
      label: `${m.user.name} (${m.role})`
    }))
  ];

  const currentAssigneeName = typeof task.assignee === 'object' ? task.assignee?.name : task.assignee || 'unassigned';

  // Check if task is blocked by incomplete tasks
  const blockedByTasks = (task.blockedBy || [])
    .map((id) => tasks.find((t) => t.id === id))
    .filter(Boolean);

  const blockingTasks = (task.blocking || [])
    .map((id) => tasks.find((t) => t.id === id))
    .filter(Boolean);

  const relatedTasks = (task.relatedTo || [])
    .map((id) => tasks.find((t) => t.id === id))
    .filter(Boolean);

  const activeBlockers = blockedByTasks.filter((t) => t.status !== 'done');
  const isBlocked = activeBlockers.length > 0;

  const candidateDependencyTasks = tasks
    .filter((t) => t.id !== task.id)
    .map((t) => ({ value: t.id, label: `${t.id} - ${t.title}` }));

  const handleAssigneeChange = (selectedName) => {
    if (selectedName === 'unassigned') {
      updateTask(task.id, { assignee: null });
      return;
    }
    const member = members.find((m) => m.user.name === selectedName);
    if (member) {
      updateTask(task.id, {
        assignee: {
          name: member.user.name,
          email: member.user.email,
          avatar: member.user.avatar
        }
      });
    } else {
      updateTask(task.id, {
        assignee: { name: selectedName, avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80' }
      });
    }
  };

  // Checklists
  const handleToggleChecklist = (checklistId) => {
    const updated = (task.checklists || []).map((c) =>
      c.id === checklistId ? { ...c, done: !c.done } : c
    );
    updateTask(task.id, { checklists: updated });
  };

  const handleAddChecklist = (e) => {
    e?.preventDefault();
    if (!newChecklistText.trim()) return;
    const newChecklist = {
      id: 'c_' + Date.now(),
      text: newChecklistText.trim(),
      done: false
    };
    updateTask(task.id, { checklists: [...(task.checklists || []), newChecklist] });
    setNewChecklistText('');
  };

  const handleDeleteChecklist = (checklistId) => {
    const updated = (task.checklists || []).filter((c) => c.id !== checklistId);
    updateTask(task.id, { checklists: updated });
  };

  // Subtasks
  const handleToggleSubtask = (subtaskId) => {
    const updated = (task.subtasks || []).map((s) =>
      s.id === subtaskId ? { ...s, done: !s.done } : s
    );
    updateTask(task.id, { subtasks: updated });
  };

  const handleAddSubtask = (e) => {
    e?.preventDefault();
    if (!newSubtaskTitle.trim()) return;
    const newSubtask = {
      id: 'sub_' + Date.now(),
      title: newSubtaskTitle.trim(),
      done: false,
      assignee: currentAssigneeName !== 'unassigned' ? currentAssigneeName : 'Rohith Prince'
    };
    updateTask(task.id, { subtasks: [...(task.subtasks || []), newSubtask] });
    setNewSubtaskTitle('');
  };

  const handleDeleteSubtask = (subtaskId) => {
    const updated = (task.subtasks || []).filter((s) => s.id !== subtaskId);
    updateTask(task.id, { subtasks: updated });
  };

  // Labels
  const handleAddLabel = (e) => {
    e?.preventDefault();
    if (!newLabelText.trim()) return;
    const currentLabels = task.labels || [];
    if (!currentLabels.includes(newLabelText.trim())) {
      updateTask(task.id, { labels: [...currentLabels, newLabelText.trim()] });
    }
    setNewLabelText('');
    setIsAddingLabel(false);
  };

  const handleRemoveLabel = (lblToRemove) => {
    const updated = (task.labels || []).filter((l) => l !== lblToRemove);
    updateTask(task.id, { labels: updated });
  };

  // Dependencies
  const handleAddDependencySubmit = (e) => {
    e?.preventDefault();
    if (!selectedDepTaskId) return;

    const currentList = task[depRelationship] || [];
    if (!currentList.includes(selectedDepTaskId)) {
      updateTask(task.id, {
        [depRelationship]: [...currentList, selectedDepTaskId]
      });

      // Reciprocal link
      const reciprocalRel = depRelationship === 'blockedBy' ? 'blocking' : depRelationship === 'blocking' ? 'blockedBy' : 'relatedTo';
      const target = tasks.find((t) => t.id === selectedDepTaskId);
      if (target) {
        const targetList = target[reciprocalRel] || [];
        if (!targetList.includes(task.id)) {
          updateTask(target.id, {
            [reciprocalRel]: [...targetList, task.id]
          });
        }
      }
    }

    setIsAddingDependency(false);
    setSelectedDepTaskId('');
  };

  const handleRemoveDependency = (relKey, depId) => {
    const updated = (task[relKey] || []).filter((id) => id !== depId);
    updateTask(task.id, { [relKey]: updated });
  };

  // Attachments
  const handleAddSampleAttachment = () => {
    const sampleFiles = [
      { name: 'architecture_diagram_v2.png', size: '1.4 MB', type: 'image' },
      { name: 'api_specifications.yaml', size: '32 KB', type: 'code' },
      { name: 'release_notes_v1.0.pdf', size: '480 KB', type: 'doc' }
    ];
    const picked = sampleFiles[Math.floor(Math.random() * sampleFiles.length)];
    const newAtt = {
      id: 'att_' + Date.now(),
      name: picked.name,
      size: picked.size,
      type: picked.type,
      uploadedAt: 'Today'
    };
    updateTask(task.id, {
      attachments: [...(task.attachments || []), newAtt]
    });
  };

  const handleDeleteAttachment = (attId) => {
    const updated = (task.attachments || []).filter((a) => a.id !== attId);
    updateTask(task.id, { attachments: updated });
  };

  // Watchers
  const isWatching = (task.watchers || []).includes('usr_101');
  const handleToggleWatch = () => {
    const currentWatchers = task.watchers || [];
    if (isWatching) {
      updateTask(task.id, { watchers: currentWatchers.filter((w) => w !== 'usr_101') });
    } else {
      updateTask(task.id, { watchers: [...currentWatchers, 'usr_101'] });
    }
  };

  // Comments with @mention support
  const handleAddComment = (e) => {
    e?.preventDefault();
    if (!newCommentText.trim()) return;
    const newComment = {
      id: 'cm_' + Date.now(),
      author: 'Rohith Prince',
      text: newCommentText.trim(),
      time: 'Just now'
    };
    updateTask(task.id, { comments: [...(task.comments || []), newComment] });
    setNewCommentText('');
  };

  const handleMentionClick = (userName) => {
    setNewCommentText((prev) => `${prev} @${userName} `);
  };

  const handleDelete = () => {
    if (window.confirm(`Permanently delete task ${task.id}?`)) {
      deleteTask(task.id);
      onClose();
    }
  };

  const totalChecklists = (task.checklists || []).length;
  const completedChecklists = (task.checklists || []).filter((c) => c.done).length;
  const checklistProgress = totalChecklists > 0 ? Math.round((completedChecklists / totalChecklists) * 100) : 0;

  const totalSubtasks = (task.subtasks || []).length;
  const completedSubtasks = (task.subtasks || []).filter((s) => s.done).length;

  return (
    <Drawer
      isOpen={!!task}
      onClose={onClose}
      title={task.id}
      subtitle={`Task Details • Status: ${task.status.replace('_', ' ')}`}
      width="680px"
      footer={
        <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button variant="danger" size="sm" icon={<Trash2 size={14} />} onClick={handleDelete}>
            Delete Task
          </Button>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              variant={isWatching ? 'secondary' : 'recessed'}
              size="sm"
              icon={<Eye size={14} color={isWatching ? 'var(--accent-teal)' : 'var(--text-muted)'} />}
              onClick={handleToggleWatch}
            >
              {isWatching ? 'Watching' : 'Watch'} ({(task.watchers || []).length})
            </Button>
            <Button variant="primary" size="sm" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
        {/* BLOCKED STATE BANNER */}
        {isBlocked && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: 'rgba(239, 68, 68, 0.12)',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <AlertTriangle size={18} color="var(--color-danger)" />
              <div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--color-danger)' }}>
                  This task is currently blocked
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Prerequisite task{' '}
                  {activeBlockers.map((b) => (
                    <strong
                      key={b.id}
                      onClick={() => setSelectedTask(b)}
                      style={{ cursor: 'pointer', textDecoration: 'underline', color: 'var(--accent-teal)', margin: '0 4px' }}
                    >
                      {b.id} ({b.title})
                    </strong>
                  ))}{' '}
                  must be marked done first.
                </div>
              </div>
            </div>
            <Badge variant="danger" size="xs">Blocked</Badge>
          </div>
        )}

        {/* Title Edit */}
        <div>
          <Input
            value={task.title}
            onChange={(e) => updateTask(task.id, { title: e.target.value })}
            placeholder="Task Title"
            style={{ fontSize: '17px', fontWeight: '800' }}
          />
        </div>

        {/* Metadata Controls Matrix (Status, Priority, Type, Assignee, Estimate, Due Date) */}
        <div
          style={{
            padding: '16px',
            backgroundColor: 'var(--surface-recessed)',
            boxShadow: 'var(--shadow-recessed-sm)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border-subtle)',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px'
          }}
        >
          <Select
            label="Status"
            options={statusOptions}
            value={task.status}
            onChange={(val) => updateTask(task.id, { status: val })}
            size="sm"
          />

          <Select
            label="Priority"
            options={priorityOptions}
            value={task.priority}
            onChange={(val) => updateTask(task.id, { priority: val })}
            size="sm"
          />

          <Select
            label="Task Type"
            options={typeOptions}
            value={task.taskType || 'task'}
            onChange={(val) => updateTask(task.id, { taskType: val })}
            size="sm"
          />

          <Select
            label="Assignee"
            options={assigneeOptions}
            value={currentAssigneeName}
            onChange={handleAssigneeChange}
            size="sm"
          />

          <Select
            label="Story Estimate"
            options={estimateOptions}
            value={task.estimate || '3 pts'}
            onChange={(val) => updateTask(task.id, { estimate: val })}
            size="sm"
          />

          <Input
            label="Due Date"
            type="date"
            value={task.dueDate || ''}
            onChange={(e) => updateTask(task.id, { dueDate: e.target.value })}
          />
        </div>

        {/* Labels Section */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
              <Tag size={15} color="var(--accent-teal)" />
              <span>Labels & Tags</span>
            </div>
            {!isAddingLabel && (
              <button
                onClick={() => setIsAddingLabel(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--accent-teal)',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Plus size={13} /> Add Tag
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
            {(task.labels || []).map((lbl) => (
              <div
                key={lbl}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '3px 8px',
                  backgroundColor: 'var(--surface-card)',
                  boxShadow: 'var(--shadow-raised-sm)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '999px',
                  fontSize: '12px',
                  color: 'var(--text-primary)',
                  fontWeight: '600'
                }}
              >
                <span>{lbl}</span>
                <button
                  onClick={() => handleRemoveLabel(lbl)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: '13px',
                    lineHeight: 1
                  }}
                >
                  ×
                </button>
              </div>
            ))}

            {isAddingLabel && (
              <form onSubmit={handleAddLabel} style={{ display: 'inline-flex', gap: '6px' }}>
                <input
                  type="text"
                  value={newLabelText}
                  onChange={(e) => setNewLabelText(e.target.value)}
                  placeholder="Tag name..."
                  autoFocus
                  style={{
                    padding: '3px 8px',
                    fontSize: '12px',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--accent-teal)',
                    backgroundColor: 'var(--surface-recessed)',
                    color: 'var(--text-primary)',
                    outline: 'none'
                  }}
                />
                <Button variant="primary" size="xs" type="submit">
                  Add
                </Button>
                <Button variant="ghost" size="xs" onClick={() => setIsAddingLabel(false)}>
                  Cancel
                </Button>
              </form>
            )}
          </div>
        </div>

        {/* Task Dependencies & Relationships */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
              <Link2 size={15} color="var(--accent-teal)" />
              <span>Dependencies & Relationships</span>
            </div>
            {!isAddingDependency && (
              <button
                onClick={() => setIsAddingDependency(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--accent-teal)',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Plus size={13} /> Link Task
              </button>
            )}
          </div>

          {/* Add Dependency Inline Selector */}
          {isAddingDependency && (
            <form
              onSubmit={handleAddDependencySubmit}
              style={{
                padding: '12px',
                backgroundColor: 'var(--surface-recessed)',
                borderRadius: 'var(--radius-md)',
                marginBottom: '10px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr', gap: '8px' }}>
                <select
                  value={depRelationship}
                  onChange={(e) => setDepRelationship(e.target.value)}
                  style={{
                    padding: '6px',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border-subtle)',
                    backgroundColor: 'var(--surface-card)',
                    fontSize: '12px',
                    color: 'var(--text-primary)'
                  }}
                >
                  <option value="blockedBy">Blocked by (Prerequisite)</option>
                  <option value="blocking">Blocks (Dependent)</option>
                  <option value="relatedTo">Relates to</option>
                </select>

                <select
                  value={selectedDepTaskId}
                  onChange={(e) => setSelectedDepTaskId(e.target.value)}
                  style={{
                    padding: '6px',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border-subtle)',
                    backgroundColor: 'var(--surface-card)',
                    fontSize: '12px',
                    color: 'var(--text-primary)'
                  }}
                >
                  <option value="">Select a task...</option>
                  {candidateDependencyTasks.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                <Button variant="ghost" size="xs" onClick={() => setIsAddingDependency(false)}>
                  Cancel
                </Button>
                <Button variant="primary" size="xs" type="submit" disabled={!selectedDepTaskId}>
                  Confirm Link
                </Button>
              </div>
            </form>
          )}

          {/* Dependencies List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {blockedByTasks.map((dep) => (
              <div
                key={dep.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  backgroundColor: 'var(--surface-card)',
                  boxShadow: 'var(--shadow-raised-sm)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-subtle)'
                }}
              >
                <div
                  onClick={() => setSelectedTask(dep)}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', flex: 1 }}
                >
                  <Badge variant={dep.status === 'done' ? 'success' : 'danger'} size="xs">
                    Blocked by
                  </Badge>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--accent-teal)' }}>{dep.id}</span>
                  <span style={{ fontSize: '12px', color: 'var(--text-primary)' }}>{dep.title}</span>
                </div>
                <button
                  onClick={() => handleRemoveDependency('blockedBy', dep.id)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '2px' }}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}

            {blockingTasks.map((dep) => (
              <div
                key={dep.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  backgroundColor: 'var(--surface-card)',
                  boxShadow: 'var(--shadow-raised-sm)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-subtle)'
                }}
              >
                <div
                  onClick={() => setSelectedTask(dep)}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', flex: 1 }}
                >
                  <Badge variant="purple" size="xs">Blocks</Badge>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--accent-teal)' }}>{dep.id}</span>
                  <span style={{ fontSize: '12px', color: 'var(--text-primary)' }}>{dep.title}</span>
                </div>
                <button
                  onClick={() => handleRemoveDependency('blocking', dep.id)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '2px' }}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}

            {relatedTasks.map((dep) => (
              <div
                key={dep.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  backgroundColor: 'var(--surface-card)',
                  boxShadow: 'var(--shadow-raised-sm)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-subtle)'
                }}
              >
                <div
                  onClick={() => setSelectedTask(dep)}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', flex: 1 }}
                >
                  <Badge variant="neutral" size="xs">Related</Badge>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--accent-teal)' }}>{dep.id}</span>
                  <span style={{ fontSize: '12px', color: 'var(--text-primary)' }}>{dep.title}</span>
                </div>
                <button
                  onClick={() => handleRemoveDependency('relatedTo', dep.id)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '2px' }}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}

            {blockedByTasks.length === 0 && blockingTasks.length === 0 && relatedTasks.length === 0 && !isAddingDependency && (
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', padding: '6px' }}>
                No task dependencies linked.
              </div>
            )}
          </div>
        </div>

        {/* File Attachments Section */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
              <Paperclip size={15} color="var(--accent-teal)" />
              <span>Attachments ({(task.attachments || []).length})</span>
            </div>
            <button
              onClick={handleAddSampleAttachment}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--accent-teal)',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <Plus size={13} /> Add Attachment
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {(task.attachments || []).map((att) => (
              <div
                key={att.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  backgroundColor: 'var(--surface-card)',
                  boxShadow: 'var(--shadow-raised-sm)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-subtle)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FileText size={16} color="var(--accent-teal)" />
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>{att.name}</div>
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{att.size} • {att.uploadedAt}</div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <button
                    onClick={() => alert(`Downloading ${att.name}...`)}
                    style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '2px' }}
                    title="Download"
                  >
                    <Download size={13} />
                  </button>
                  <button
                    onClick={() => handleDeleteAttachment(att.id)}
                    style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '2px' }}
                    title="Delete"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Description & Specs */}
        <div>
          <Textarea
            label="Description & Specifications"
            value={task.description || ''}
            onChange={(e) => updateTask(task.id, { description: e.target.value })}
            rows={4}
            placeholder="Detailed description, steps, and technical specifications..."
          />
        </div>

        {/* Subtasks Section */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Layers size={15} color="var(--accent-teal)" />
              <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                Subtasks ({completedSubtasks} / {totalSubtasks})
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
            {(task.subtasks || []).map((sub) => (
              <div
                key={sub.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  backgroundColor: sub.done ? 'var(--surface-recessed)' : 'var(--surface-card)',
                  boxShadow: sub.done ? 'var(--shadow-recessed-sm)' : 'var(--shadow-raised-sm)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-subtle)'
                }}
              >
                <div
                  onClick={() => handleToggleSubtask(sub.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flex: 1 }}
                >
                  <input
                    type="checkbox"
                    checked={sub.done}
                    onChange={() => {}}
                    style={{ accentColor: 'var(--accent-teal)', cursor: 'pointer' }}
                  />
                  <span
                    style={{
                      fontSize: '13px',
                      color: sub.done ? 'var(--text-muted)' : 'var(--text-primary)',
                      textDecoration: sub.done ? 'line-through' : 'none'
                    }}
                  >
                    {sub.title}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {sub.assignee && (
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                      {sub.assignee}
                    </span>
                  )}
                  <button
                    onClick={() => handleDeleteSubtask(sub.id)}
                    style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '2px' }}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={handleAddSubtask} style={{ display: 'flex', gap: '8px' }}>
            <Input
              value={newSubtaskTitle}
              onChange={(e) => setNewSubtaskTitle(e.target.value)}
              placeholder="Add a subtask..."
            />
            <Button variant="secondary" size="md" icon={<Plus size={15} />} type="submit">
              Add
            </Button>
          </form>
        </div>

        {/* Checklists Section */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ListTodo size={15} color="var(--accent-teal)" />
              <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                Checklist ({completedChecklists} / {totalChecklists})
              </span>
            </div>
            {totalChecklists > 0 && (
              <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--accent-teal)' }}>
                {checklistProgress}%
              </span>
            )}
          </div>

          {totalChecklists > 0 && (
            <div
              style={{
                height: '4px',
                borderRadius: '999px',
                backgroundColor: 'var(--surface-recessed)',
                overflow: 'hidden',
                marginBottom: '10px'
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${checklistProgress}%`,
                  backgroundColor: 'var(--accent-teal)',
                  borderRadius: '999px',
                  transition: 'width 0.25s ease'
                }}
              />
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
            {(task.checklists || []).map((item) => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  backgroundColor: item.done ? 'var(--surface-recessed)' : 'var(--surface-card)',
                  boxShadow: item.done ? 'var(--shadow-recessed-sm)' : 'var(--shadow-raised-sm)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-subtle)'
                }}
              >
                <div
                  onClick={() => handleToggleChecklist(item.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flex: 1 }}
                >
                  <input
                    type="checkbox"
                    checked={item.done}
                    onChange={() => {}}
                    style={{ accentColor: 'var(--accent-teal)', cursor: 'pointer' }}
                  />
                  <span
                    style={{
                      fontSize: '13px',
                      color: item.done ? 'var(--text-muted)' : 'var(--text-primary)',
                      textDecoration: item.done ? 'line-through' : 'none'
                    }}
                  >
                    {item.text}
                  </span>
                </div>

                <button
                  onClick={() => handleDeleteChecklist(item.id)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '2px' }}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>

          <form onSubmit={handleAddChecklist} style={{ display: 'flex', gap: '8px' }}>
            <Input
              value={newChecklistText}
              onChange={(e) => setNewChecklistText(e.target.value)}
              placeholder="Add a checklist item..."
            />
            <Button variant="secondary" size="md" icon={<Plus size={15} />} type="submit">
              Add
            </Button>
          </form>
        </div>

        {/* Collaboration & Comments with @mentions */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <MessageSquare size={16} color="var(--accent-teal)" />
              <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                Discussion & Mentions ({(task.comments || []).length})
              </span>
            </div>
            {/* Quick Mentions Bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '2px' }}>
                <AtSign size={11} /> Mention:
              </span>
              {members.map((m) => (
                <button
                  key={m.user.id}
                  type="button"
                  onClick={() => handleMentionClick(m.user.name)}
                  style={{
                    background: 'none',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: '4px',
                    padding: '1px 5px',
                    fontSize: '10px',
                    color: 'var(--accent-teal)',
                    cursor: 'pointer'
                  }}
                >
                  {m.user.name.split(' ')[0]}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' }}>
            {(task.comments || []).map((cm) => (
              <div
                key={cm.id}
                style={{
                  padding: '12px',
                  backgroundColor: 'var(--surface-card)',
                  boxShadow: 'var(--shadow-raised-sm)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-subtle)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-primary)' }}>
                    {cm.author}
                  </span>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{cm.time}</span>
                </div>
                <div
                  style={{
                    fontSize: '13px',
                    color: 'var(--text-secondary)',
                    lineHeight: '1.4'
                  }}
                >
                  {cm.text}
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={handleAddComment} style={{ display: 'flex', gap: '8px' }}>
            <Input
              value={newCommentText}
              onChange={(e) => setNewCommentText(e.target.value)}
              placeholder="Write a comment or type @Name to mention..."
            />
            <Button variant="primary" size="md" type="submit">
              Post
            </Button>
          </form>
        </div>
      </div>
    </Drawer>
  );
};
